import {
  Card,
  CrudMode,
  DataDisplay,
  formatSchoolYear,
  GeneralValidationErrors,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { StudentAbsence, StudentAbsenceEditResource, StudentAbsenceViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const StudentAbsencePageBody = (props: {
  crudMode: CrudMode;
  absence: StudentAbsence;
  setAbsence: (value: StudentAbsence) => void;
  viewResource: StudentAbsenceViewResource;
  editResource: StudentAbsenceEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const generationValidationErrors = props.validationErrors[pn<StudentAbsence>("generalValidationErrors")];
  const termCount: number | undefined =
    props.viewResource.termTypes?.find((x) => x.id === props.absence.termTypeId)?.termCount || 0;

  const schoolYearField = () => {
    const propertyName = pn<StudentAbsence>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.absence.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.absence.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentAbsence>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.absence.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const termTypeField = () => {
    const propertyName = pn<StudentAbsence>("termTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.termType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.termTypes?.find((x) => x.id === props.absence.termTypeId)}
        options={props.viewResource.termTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.termTypeId = value?.id;
              draft.termNumber = undefined; // Assuming this is required as per your snippet
            })
          )
        }
      />
    );
  };

  const termNumberField = (termCount: number) => {
    const propertyName = pn<StudentAbsence>("termNumber");
    const property = properties.find((x) => x.propertyName === propertyName);
    const termNumbers: number[] = Array.from({ length: termCount }, (_, i) => i + 1);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.termNumber}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={termNumbers.find((x) => x === props.absence.termNumber)}
        options={termNumbers}
        getOptionLabel={(option) => option.toString()}
        onChange={(e, value) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.termNumber = value;
            })
          )
        }
      />
    );
  };

  const excusedAbsencesField = () => {
    const propertyName = pn<StudentAbsence>("excusedAbsences");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.excusedAbsences}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.absence.excusedAbsences}
        onChange={(e) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.excusedAbsences = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const unexcusedAbsencesField = () => {
    const propertyName = pn<StudentAbsence>("unexcusedAbsences");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.unexcusedAbsences}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.absence.unexcusedAbsences}
        onChange={(e) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.unexcusedAbsences = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const daysAttendedField = () => {
    const propertyName = pn<StudentAbsence>("daysAttended");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.daysAttended}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.absence.daysAttended}
        onChange={(e) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.daysAttended = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const daysEnrolledField = () => {
    const propertyName = pn<StudentAbsence>("daysEnrolled");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.absence.daysEnrolled}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.absence.daysEnrolled}
        onChange={(e) =>
          props.setAbsence(
            produce(props.absence, (draft) => {
              draft.daysEnrolled = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      {generationValidationErrors && (
        <Grid item xs={fullFieldBreakpoints}>
          <GeneralValidationErrors errors={generationValidationErrors} />
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {schoolYearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {schoolField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {termTypeField()}
      </Grid>

      {termCount > 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {termNumberField(termCount)}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {excusedAbsencesField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {unexcusedAbsencesField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {daysAttendedField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {daysEnrolledField()}
      </Grid>
    </Grid>
  );
};
